/**
 * The words on the review screen, written for the same clerk as the intake — see
 * `pages/ArrangementBuilder/copy.ts` for the rules. One short sentence, plain language, and
 * the consequence rather than the definition.
 */

export const DETAIL_COPY = {
  /** [P6c §6] Opens in a new tab, so the order can be printed beside the screen it came from. */
  print: 'Open the printable order sheet, with every named officer, in a new tab.',
  /* [FIX-P6b.1] Supervision, which this page collected and never showed back. */
  commandTitle: 'Command',
  commandHint: 'Who is in charge, as the order was issued. Command roles are never counted toward force.',
  commandEmpty: 'This order names nobody in command.',
  /* ⚠️ [P6b.2] PER CARD, so an order with supervision on one duty point and none on another
     says so on both rather than reading as though the whole order had some. */
  commandPointEmpty: 'Nobody in command at this duty point.',
  commandNobody: 'Nobody named',
  commandOneOff: 'One-off',
  commandUnnamedRole: 'Unnamed role',
  /* ── [D4.3 §5] the officer surface ────────────────────────────────────────────── */

  officersHeading: 'Officers on this duty',
  officersLoading: 'Loading the names…',
  officersEmpty: 'This duty asks for nobody.',
  /**
   * ⚠️ THE SERVER DECIDED THIS, not a clock read here. A duty with a fixed time closes at
   * `ends_at`; a whole-day one at the end of its duty date in Asia/Kolkata. One definition,
   * in `fulfilment.service.ts`, published on the sheet as `swapWindowOpen`.
   */
  /**
   * ⚠️ [amended C3c §3d] THE SENTENCE IS TRUE AGAIN. [PRE-P6.2] narrowed the swap window to
   * `starts_at + 1h`, which could close while the duty was STILL RUNNING — so "this duty is
   * over" was false for up to eight hours. §3b restored swapping to the end of the shift, and
   * this string now fires only when both windows have closed.
   */
  windowClosed: 'This duty is over, so its names can no longer be changed.',
  droppedHeading: 'No longer required',
  slotMoved: 'That slot has changed since this page loaded. Reopen the order and try again.',
  swapTitle: 'Why is this changing?',
  swapIntro: 'This share is already marked complete. A short reason helps whoever reads the order later.',
  swapOptional: 'Optional. Leave it blank and the swap still goes through.',
  swapLabel: 'Reason (optional)',
  swapConfirm: 'Replace',

  /** [P4] Into the fill screen, from the review step. */
  nameOfficers: 'Name officers',
  dutiesTitle: 'Duties',
  dutiesHint: 'One for each place, on each shift, on each date this order covers.',
  dutiesEmpty: 'Nothing has been issued yet. Publishing this order creates its duties.',

  /* ── [D4.4 §1a] which duty point a duty came from ─────────────────────────────── */

  /**
   * ⚠️ THE WORD THE OPERATOR USES, not the word the schema uses. A card on the intake is
   * "a place and its times"; naming the column `Duty point` here and nothing there would
   * make the two screens read as different objects.
   */
  dutyPointColumn: 'Duty point',
  dutyPointFilter: 'Duty point',
  allDutyPoints: 'All duty points',
  /** Shown only on a multi-card order, which is the only order where it is not obvious. */
  dutiesMultiPointHint: 'This order has several duty points. Each duty below names the one it came from.',

  /**
   * ⚠️ The consequence, not the mechanism. An operator does not need the word "detach";
   * they need to know this shift stops following the others for good.
   */
  detach: 'Sets this shift apart. Later changes to the others will leave this one alone.',
  detachedFlag: 'Changed on its own',
  detachConfirmTitle: 'Set this shift apart?',
  detachConfirmBody:
    'It will keep the force it has now, and updates you make to the other shifts will no longer reach it.',

  restamp: 'Copies the force you have set on the order onto every shift that has not been set apart.',
  restampConfirmTitle: 'Update the duties?',
  /** Says what will change AND what will not — before the click, never after. */
  restampConfirmBody: (updated: number, skipped: number): string =>
    `${String(updated)} ${updated === 1 ? 'duty' : 'duties'} will be updated. ` +
    `${String(skipped)} set apart earlier will be left alone.`,
  restampDone: (updated: number, skipped: number): string =>
    `${String(updated)} updated, ${String(skipped)} left alone.`,

  edit: 'Open this order to change it.',
  editFrozen: 'The dates and places of a published order cannot be changed. You can still edit the wording.',
  duplicate: 'Starts a new order with everything copied except the date.',
  /*
   * ⚠️ [amended C2a §3] The refusal for the two AUTHORING controls on this page. It names the
   * state of the deployment rather than the person (`DEACTIVATED`'s rule) and says what the
   * reader can still do, because they can still do most of it.
   */
  mayNotAuthor:
    'Arrangements are edited by the CP office on this deployment. You can still name, swap and delegate officers on this one.',

  /* ── [D4.4 §5] taking a published order back to draft ─────────────────────────── */

  /**
   * ⚠️ "Take back to draft" RATHER THAN "Un-publish" OR "Withdraw". An operator who reads
   * "withdraw" will look for the order in the Archive, where it is not — it is still a
   * current order. The label says where it goes.
   */
  unpublish: 'Take this order back to draft so its duty points can be changed.',
  unpublishTitle: 'Take this order back to draft?',
  unpublishOk: 'Take back to draft',
  unpublishReasonLabel: 'Why is this being taken back?',
  /** ⚠️ Required. It reaches the audit row, which is where "why" is asked a week later. */
  unpublishReasonHint: 'This is recorded against the order.',
  /** Tier FREE — nobody has been told about a specific person. */
  unpublishFree: 'Nobody has been named yet, so no one has to be told.',
  /** Tier DISCARDS_ASSIGNMENTS — the two numbers, because they are different questions. */
  unpublishDiscards: (assignments: number, officers: number): string =>
    `${String(assignments)} name${assignments === 1 ? '' : 's'} already entered will be discarded, ` +
    `affecting ${String(officers)} officer${officers === 1 ? '' : 's'}.`,
  unpublishTold: (units: number): string =>
    `${String(units)} division${units === 1 ? '' : 's'} and unit${units === 1 ? '' : 's'} will be told it has been ` +
    'taken back.',
  unpublishDuties: (duties: number): string =>
    `${String(duties)} ${duties === 1 ? 'duty' : 'duties'} will be discarded. Publishing again recreates them.`,
  /** Tier BLOCKED — the same refusal archive raises, said before the click rather than after. */
  unpublishBlocked: 'This cannot be taken back because one of its duties has already started.',
  unpublishBlockedEvent: 'A duty under this order has already been carried out, so it can no longer be changed.',

  strip: 'The dates this order covers.',
} as const;

/**
 * ⚠️ [C2b §4f] THE WORDS FOR A CORRIGENDUM FAMILY, IN ONE PLACE.
 *
 * A corrigendum names the order it corrects; a corrected order says only THAT it was
 * corrected and how many times — never a list, because the members are ordinary orders that
 * are found by their own reference and a second list of links is a second place the family is
 * enumerated (`arrangementGroups.ts`'s own reason for nesting client-side and nothing more).
 */
export const CORRIGENDUM_COPY = {
  parentNote: (count: number): string =>
    count === 1
      ? 'One corrigendum has been issued against this order.'
      : `${count} corrigenda have been issued against this order.`,
  /** ⚠️ Never a blank — a blank reads as "this is not a corrigendum", which is the opposite. */
  parentGone: 'The order this corrects is no longer on record.',
  /** ⚠️ Its OWN duty and name counts — never folded into the parent's. */
  unpublishCascade: (reference: string, duties: number, names: number): string =>
    `${reference} goes back to draft too — ${duties} ${duties === 1 ? 'duty' : 'duties'} and ` +
    `${names} ${names === 1 ? 'name' : 'names'} discarded with it.`,
} as const;
